import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

const gbp = (n) => `£${n.toFixed(2)}`;

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, canIncrease } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 150 || subtotal === 0 ? 0 : 6.99;

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-testid="cart-drawer" className="glass-strong border-slate-200 w-[90vw] sm:w-[440px] flex flex-col p-0">
        <SheetHeader className="border-b border-slate-200 p-5">
          <SheetTitle className="font-display text-xl text-slate-900">Your Cart</SheetTitle>
          <SheetDescription className="sr-only">Items currently in your shopping cart</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <ShoppingBag className="h-10 w-10 text-slate-600" />
            <p className="text-slate-600">Your cart is empty.</p>
            <Button variant="outline" className="border-slate-200" onClick={() => { setOpen(false); navigate("/shop"); }}>
              Explore Research
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.map((item) => (
                <div key={item.key} data-testid={`cart-item-${item.id}`} className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3">
                  <img src={item.image} alt={item.name} className="h-16 w-16 rounded-md object-cover" />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900">{item.name}</p>
                      <button onClick={() => removeItem(item.key)} data-testid={`cart-remove-${item.id}`} className="text-slate-500 hover:text-red-400">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {item.type === "bundle" && <span className="mt-0.5 w-fit text-[10px] font-mono2 uppercase tracking-wider text-sky-400">Bundle</span>}
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center rounded-md border border-slate-200">
                        <button data-testid={`cart-qty-dec-${item.id}`} onClick={() => updateQty(item.key, item.qty - 1)} className="px-2 py-1 text-slate-700 hover:text-slate-900"><Minus className="h-3 w-3" /></button>
                        <span className="w-7 text-center text-sm text-slate-800">{item.qty}</span>
                        <button data-testid={`cart-qty-inc-${item.id}`} onClick={() => updateQty(item.key, item.qty + 1)} disabled={!canIncrease(item.key)} className="px-2 py-1 text-slate-700 hover:text-slate-900 disabled:opacity-30"><Plus className="h-3 w-3" /></button>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{gbp(item.price * item.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 p-5 space-y-3">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Subtotal</span><span className="text-slate-800">{gbp(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-600">
                <span>Shipping</span><span className="text-slate-800">{shipping === 0 ? "Free" : gbp(shipping)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-3 text-base font-semibold">
                <span className="text-slate-900">Total</span>
                <span className="metallic-text">{gbp(subtotal + shipping)}</span>
              </div>
              <Button data-testid="cart-checkout-button" onClick={checkout} className="w-full btn-metallic font-semibold h-11">
                Proceed to Checkout
              </Button>
              <p className="text-center text-[11px] text-slate-500">Lawful research & educational use only.</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
